import React, { useEffect, useState } from "react";
import axios from "axios";
import awsConfig from "../aws-exports"; // Import backend configuration
import { useNavigate } from "react-router-dom"; // Import useNavigate
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import StarIcon from "@mui/icons-material/Star";
import PersonIcon from "@mui/icons-material/Person";
import { jwtDecode } from "jwt-decode"; // Correct import
import "./ProfilePage.css"; // Import the new CSS file

const ProfilePage = () => {
  const navigate = useNavigate(); // Initialize navigate
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "@",
    phoneNumber: "+1",
    macAddress1: "",
    macAddress2: "",
    macAddress3: "",
    macAddress4: "",
    companyName: "",
  });

  // Extract email from idToken
  const extractEmailFromToken = (idToken) => {
    try {
      const decodedToken = jwtDecode(idToken);
      return decodedToken.email || ""; // Return the email from the token
    } catch (error) {
      console.error("Error decoding idToken:", error);
      return "";
    }
  };

  // Fetch profile data from backend
  const fetchProfile = async () => {
    try {
      const idToken = localStorage.getItem("id_token");

      if (!idToken) {
        throw new Error("User is not authenticated. ID token is missing.");
      }

      console.log("Fetching profile with ID Token:", idToken);

      const response = await axios.get(`${awsConfig.baseUrl}profile`, {
        headers: { Authorization: `Bearer ${idToken}` },
        timeout: 10000, // Increase timeout to 10 seconds
      });

      console.log("Profile fetched successfully:", response.data);
      setProfile(response.data);
      setFormData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        // Profile not found, create a new one
        console.log("Profile not found. Creating a new profile...");
        await createProfileWithRetry();
      } else {
        console.error("Error fetching profile:", err);
        setError(err.response?.data?.message || "Failed to fetch profile.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Create a new profile with retry mechanism
  const createProfileWithRetry = async (retries = 3, delay = 2000) => {
    for (let i = 0; i < retries; i++) {
      try {
        const idToken = localStorage.getItem("id_token");

        if (!idToken) {
          throw new Error("User is not authenticated. ID token is missing.");
        }

        // Extract email from the idToken
        const email = extractEmailFromToken(idToken);

        if (!email) {
          throw new Error("Email not found in the ID token.");
        }

        // Default profile with email from the token
        const defaultProfile = {
          firstname: "",
          lastname: "",
          email: email, // Use the email from the token
          phoneNumber: "+1", // Phone number is required, but we'll validate it before saving
          macAddress1: "",
          macAddress2: "",
          macAddress3: "",
          macAddress4: "",
          companyName: "",
        };

        const response = await axios.post(`${awsConfig.baseUrl}profile`, defaultProfile, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          timeout: 10000, // Increase timeout to 10 seconds
        });

        console.log("Profile created successfully:", response.data);
        setProfile(response.data);
        setFormData(response.data);
        return; // Exit the retry loop on success
      } catch (err) {
        console.error(`Attempt ${i + 1} failed:`, err);

        if (i === retries - 1) {
          // If all retries fail, set an error message
          setError(err.response?.data?.error || "Failed to create profile.");
        } else {
          // Wait for the specified delay before retrying
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }
  };

  // Handle form input change
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Validate email and phoneNumber
  const validateForm = () => {
    if (!formData.email || !formData.phoneNumber) {
      setError("Email and Phone Number are required fields.");
      return false;
    }
    return true;
  };

  // Update profile
  const saveProfile = async () => {
    try {
      setLoading(true);
      setSuccessMessage(null);
      setError(null);

      // Validate email and phoneNumber
      if (!validateForm()) {
        return;
      }

      const idToken = localStorage.getItem("id_token");
      if (!idToken) {
        throw new Error("User is not authenticated. ID token is missing.");
      }

      console.log("Saving profile with updated data:", formData);

      const response = await axios.put(`${awsConfig.baseUrl}profile`, formData, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        timeout: 10000, // Increase timeout to 10 seconds
      });

      setProfile(response.data.data); // Update profile with the returned data
      setIsEditing(false);
      setSuccessMessage("Profile updated successfully!");
    } catch (err) {
      console.error("Error updating profile:", err);
      setError(err.response?.data?.error || "Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div style={{ color: "red" }}>Error: {error}</div>;

  return (
    <div className="dashboard-container">
      <aside className="sidebar">
        <div className="sidebar-header">Chameye</div>
        <List>
          <ListItem
            button
            onClick={() => navigate("/dashboard")}
            style={{ cursor: "pointer" }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#1976d2")
            }
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "")}
          >
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard Page" />
          </ListItem>
          <ListItem
            button
            onClick={() => navigate("/profile")}
            style={{ cursor: "pointer" }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#1976d2")
            }
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "")}
          >
            <ListItemIcon>
              <LightbulbIcon />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </ListItem>
          <ListItem
            button
            onClick={() => navigate("/report")}
            style={{ cursor: "pointer" }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#1976d2")
            }
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "")}
          >
            <ListItemIcon>
              <StarIcon />
            </ListItemIcon>
            <ListItemText primary="Report" />
          </ListItem>
        </List>
        <Divider />
        <List>
          <ListItem
            button
            onClick={() => navigate("/logout")}
            style={{ cursor: "pointer" }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#1976d2")
            }
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "")}
          >
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="Sign Out" />
          </ListItem>
        </List>
      </aside>
      <main className="content">
        <h1>Profile Page</h1>

        {successMessage && <div className="success-message">{successMessage}</div>}
        {error && <div className="error-message">{error}</div>}

        {profile ? (
          <div className="profile-form">
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                name="firstname"
                value={formData.firstname || ""}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastname"
                value={formData.lastname || ""}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email || ""}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber || ""}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName || ""}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>MAC Address 1</label>
              <input
                type="text"
                name="macAddress1"
                value={formData.macAddress1 || ""}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>MAC Address 2</label>
              <input
                type="text"
                name="macAddress2"
                value={formData.macAddress2 || ""}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>MAC Address 3</label>
              <input
                type="text"
                name="macAddress3"
                value={formData.macAddress3 || ""}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-group">
              <label>MAC Address 4</label>
              <input
                type="text"
                name="macAddress4"
                value={formData.macAddress4 || ""}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>

            {!isEditing ? (
              <button className="edit-button" onClick={() => setIsEditing(true)}>
                Edit Profile
              </button>
            ) : (
              <div className="action-buttons">
                <button className="save-button" onClick={saveProfile}>
                  Save Changes
                </button>
                <button className="cancel-button" onClick={() => setIsEditing(false)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        ) : (
          <p>No profile information found.</p>
        )}
      </main>
    </div>
  );
};

export default ProfilePage;